import TxpContentPlugin from './TxpContentPlugin';
import closeNotesEditor from '../actions/closeNotesEditor';
import onForwardeesChanged from '../actions/onForwardeesChanged';
import onNotifyOrganizerToggled from '../actions/onNotifyOrganizerToggled';
import getNotesEditorViewState from '../selectors/getNotesEditorViewState';
import getDiscardButtonStyle from '../utils/getDiscardButtonStyle';
import { BorderIdentifier, notesEditorBorderColors } from '../utils/getNotesEditorBorderStyles';
import getRSVPButtonStyle from '../utils/getRSVPButtonStyle';
import { ITheme } from '@uifabric/styling';
import { Customizer } from '@uifabric/utilities';
import { observer } from 'mobx-react';
import { Icon } from 'office-ui-fabric-react/lib/Icon';
import { Label } from 'office-ui-fabric-react/lib/Label';
import { IToggle, Toggle } from 'office-ui-fabric-react/lib/Toggle';
import { logUsage } from 'owa-analytics';
import { getCommands } from 'owa-calendar-hotkeys';
import CalendarTime from 'owa-calendar-time-host/lib/schema/CalendarTime';
import { ClientItemId } from 'owa-client-ids';
import { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import { getOptimalTextColorString, themeFromColorScheme } from 'owa-color-utils/lib/colorUtils';
import { getToggleButtonTheme } from 'owa-component-styling-utils';
import { ControlIcons } from 'owa-control-icons';
import { DraggableItemTypes } from 'owa-dnd/lib/utils/DraggableItemTypes';
import ReactEditor from 'owa-editor/lib/components/ReactEditor';
import { addGlobalHotkey } from 'owa-hotkeys';
import InfoBar from 'owa-info-bar/lib/components/InfoBar';
import loc, { format } from 'owa-localize';
import { acceptButtonText } from 'owa-locstrings/lib/strings/acceptbuttontext.locstring.json';
import { declineButtonText } from 'owa-locstrings/lib/strings/declinebuttontext.locstring.json';
import { editorAriaLabel } from 'owa-locstrings/lib/strings/editorarialabel.locstring.json';
import { editorPlaceholderText } from 'owa-locstrings/lib/strings/editorplaceholdertext.locstring.json';
import { errorMessageNoRecipentsMailCanNotBeSend } from 'owa-locstrings/lib/strings/errormessagenorecipentsmailcannotbesend.locstring.json';
import { saveButtonLabel } from 'owa-locstrings/lib/strings/savebuttonlabel.locstring.json';
import { sendButtonString } from 'owa-locstrings/lib/strings/sendbuttonstring.locstring.json';
import { tentativeButtonText } from 'owa-locstrings/lib/strings/tentativebuttontext.locstring.json';
import { AttendeeWell } from 'owa-readwrite-recipient-well-fabric/lib/components/PeoplePickerTypes';
import ReadWriteFabricRecipientWell from 'owa-readwrite-recipient-well-fabric/lib/components/ReadWriteFabricRecipientWell';
import RecipientWellLabelWithContactPicker from 'owa-recipient-well-label/lib/components/RecipientWellLabelWithContactPicker';
import AttendeePersonaProps from 'owa-readwrite-recipient-well-fabric/lib/store/schema/AttendeePersonaProps';
import convertPersonaToRecipientViewState from 'owa-readwrite-recipient-well-fabric/lib/utils/convertPersonaToRecipientViewState';
import FindRecipientPersonaType from 'owa-recipient-common/lib/store/schema/FindRecipientPersonaType';
import ReadWriteRecipientViewState from 'owa-recipient-common/lib/store/schema/ReadWriteRecipientViewState';
import { RecipientType } from 'owa-recipient-common/lib/store/schema/RecipientType';
import ResponseTypeType from 'owa-service/lib/contract/ResponseTypeType';
import { getPaletteAsRawColors } from 'owa-theme';
import * as React from 'react';
import { EditorPlugin } from 'roosterjs-editor-core';
import { timestamp } from 'owa-datetime';
import {
    cancelFormGroupEditorLabelAria,
    forwardFormGroupEditorLabelAria,
    respondFormGroupEditorLabelAria,
    notifyOrganiserLabel,
    editorDescriptionAriaLabel,
    discardButtonString,
} from './NotesEditor.locstring.json';

import {
    InfoBarMessageViewState,
    InfoBarMessageRank,
    InfoBarMessageSource,
} from 'owa-info-bar/lib/schema/InfoBarMessageViewState';
import {
    DefaultButton,
    IButton,
    IButtonStyles,
    PrimaryButton,
} from 'office-ui-fabric-react/lib/Button';
import {
    convertRecipientsToAttendees,
    convertAttendeesToRecipientWellViewStates,
} from '../utils/recipientAttendeeConversions';

let styles = require('./NotesEditor.scss');
let classNames = require('classnames/bind').bind(styles);

export interface NotesEditorProps {
    id: ClientItemId;
    colorScheme: ColorScheme;
    isNarrow: boolean;
    renderSelectedTime?: (selectedTime: CalendarTime, colorScheme: ColorScheme) => JSX.Element;
}
export interface NotesEditorState {
    hasForwardeesLengthError: boolean;
}

type EditorFocusComponent = IButton | IToggle | ReadWriteFabricRecipientWell<AttendeePersonaProps>;
@observer
export default class NotesEditor extends React.Component<NotesEditorProps, NotesEditorState> {
    state = {
        hasForwardeesLengthError: false,
    };

    private sendButton: IButton;
    private forwardRecipients: ReadWriteFabricRecipientWell<AttendeePersonaProps>;
    private notifyOrganizerToggle: IToggle;
    private reactEditor: ReactEditor;
    private plugins: EditorPlugin[];

    private hotkeyDisposeFunctions: (Promise<() => void>)[] = [];

    private handleForwardeesChanged = (recipients: ReadWriteRecipientViewState[]) => {
        if (recipients.length) {
            // Clear error as recipients are added
            this.setState({
                hasForwardeesLengthError: false,
            });
        }

        onForwardeesChanged(this.props.id, convertRecipientsToAttendees(recipients));
    };

    constructor(props) {
        super(props);
        this.plugins = [new TxpContentPlugin(getNotesEditorViewState(props.id))];
    }

    render() {
        const { isNarrow, colorScheme, renderSelectedTime, id } = this.props;
        const calendarTheme = themeFromColorScheme(colorScheme);
        const store = getNotesEditorViewState(id);

        const {
            sendResponse,
            showNotifyToggle,
            forwardees,
            iconName,
            titleText,
            proposedTime,
            isDeclinedSeriesInstance,
        } = store;

        let rsvpButtonStyle: IButtonStyles = getRSVPButtonStyle(colorScheme);

        let styleWithColor = {};

        const isTextEnabled: boolean = sendResponse;
        const greyBorderSet: number = !isTextEnabled
            ? BorderIdentifier.Top |
              BorderIdentifier.Right |
              BorderIdentifier.Bottom |
              BorderIdentifier.Left
            : undefined;
        const isForwardeesEditor: boolean =
            forwardees != null || this.state.hasForwardeesLengthError;

        // for the editor
        let editorStyle = classNames(
            styles.editor,
            notesEditorBorderColors(
                colorScheme,
                isForwardeesEditor ? BorderIdentifier.Top : greyBorderSet,
                undefined,
                isForwardeesEditor
            )
        );

        // for the recipient well

        let recipientStyle = classNames(
            styles.recipientWell,
            notesEditorBorderColors(
                colorScheme,
                this.state.hasForwardeesLengthError ? BorderIdentifier.Top : undefined,
                BorderIdentifier.Bottom // always exclude the bottom border
            )
        );

        // for the infobar
        let infoBarStyle = classNames(
            styles.infoBarContainer,
            notesEditorBorderColors(colorScheme, undefined, BorderIdentifier.Bottom) // never have the bottom border
        );

        if (colorScheme) {
            styleWithColor = {
                color: getOptimalTextColorString(
                    colorScheme.calLighterAlt, // CommandBar background color
                    colorScheme,
                    colorScheme.calDarkAlt
                ),
            };
        }

        let groupAriaLabel: string;

        if (store.isCancelledEvent) {
            groupAriaLabel = loc(cancelFormGroupEditorLabelAria);
        } else if (store.forwardees) {
            groupAriaLabel = loc(forwardFormGroupEditorLabelAria);
        } else {
            groupAriaLabel = loc(respondFormGroupEditorLabelAria);
        }

        return (
            <div aria-label={groupAriaLabel} role="group" className={styles.container}>
                <div
                    className={classNames(
                        { [styles.titleAndNotifyToggle]: titleText },
                        { narrow: isNarrow }
                    )}>
                    {titleText && (
                        <div className={classNames(styles.titleRow, { narrow: isNarrow })}>
                            <Icon
                                className={styles.icon}
                                iconName={iconName}
                                style={{
                                    color: getOptimalTextColorString(
                                        colorScheme.calLighterAlt, // CommandBar background color
                                        colorScheme,
                                        colorScheme.calDark
                                    ),
                                }}
                            />
                            <span className={styles.titleText} style={styleWithColor}>
                                {titleText}
                            </span>
                            {proposedTime &&
                                renderSelectedTime &&
                                renderSelectedTime(proposedTime, colorScheme)}
                        </div>
                    )}
                    {showNotifyToggle && (
                        <div className={classNames(styles.toggleContainer, { narrow: isNarrow })}>
                            <Label
                                style={
                                    sendResponse
                                        ? styleWithColor
                                        : {
                                              color: getOptimalTextColorString(
                                                  colorScheme.calLighterAlt, // CommandBar background color
                                                  colorScheme,
                                                  getPaletteAsRawColors().neutralPrimary
                                              ),
                                          }
                                }
                                className={classNames(styles.toggleLabel, { narrow: isNarrow })}>
                                {loc(notifyOrganiserLabel)}
                            </Label>
                            <Customizer settings={{ theme: getToggleButtonTheme(colorScheme) }}>
                                <Toggle
                                    ariaLabel={loc(notifyOrganiserLabel)}
                                    componentRef={this.notifyOrganizerRef}
                                    className={classNames(styles.toggle, { narrow: isNarrow })}
                                    defaultChecked={sendResponse}
                                    label={null}
                                    onChanged={this.onToggled}
                                    checked={sendResponse}
                                />
                            </Customizer>
                        </div>
                    )}
                </div>
                <div
                    className={classNames(styles.body, {
                        notesEditorDisabled: showNotifyToggle && !sendResponse,
                        narrow: isNarrow,
                    })}>
                    <InfoBar
                        className={infoBarStyle}
                        messages={this.getNoForwardeesErrorInfoBarViewState(
                            this.state.hasForwardeesLengthError
                        )}
                    />
                    {store.forwardees && (
                        <div className={recipientStyle}>
                            <AttendeeWell
                                ref={this.forwardRecipientsRef}
                                defaultSelectedItems={[]}
                                draggableItemType={DraggableItemTypes.ReadWriteRecipient}
                                recipientType={RecipientType.To}
                                onItemsChanged={this.handleForwardeesChanged}
                                onRenderWellLabel={this.renderFabricWellLabel(calendarTheme)}
                                theme={calendarTheme}
                                convertInitialItemsToWellItems={
                                    convertAttendeesToRecipientWellViewStates
                                }
                            />
                        </div>
                    )}
                    {isTextEnabled ? (
                        <ReactEditor
                            viewState={store}
                            classNames={classNames(editorStyle, {
                                narrow: isNarrow,
                                hasAttendees: !!store.forwardees,
                            })}
                            updateOnKeyPress={true}
                            plugins={this.plugins}
                            watermark={loc(editorPlaceholderText)}
                            ref={this.reactEditorRef}
                            ariaLabel={format(
                                loc(editorDescriptionAriaLabel),
                                titleText,
                                loc(editorAriaLabel)
                            )}
                        />
                    ) : (
                        showNotifyToggle && (
                            <textarea
                                className={classNames(editorStyle, {
                                    disabled: true,
                                    narrow: isNarrow,
                                })}
                                disabled={true}>
                                {loc(editorPlaceholderText)}
                            </textarea>
                        )
                    )}
                </div>
                <div className={styles.responseButtons}>
                    {this.useSendDiscardButtons() ? (
                        <Customizer settings={{ theme: calendarTheme }}>
                            <PrimaryButton
                                className={styles.sendButton}
                                onClick={this.onSend(null)}>
                                {store.sendResponse ? loc(sendButtonString) : loc(saveButtonLabel)}
                            </PrimaryButton>
                            <DefaultButton
                                className={styles.discardButton}
                                styles={getDiscardButtonStyle(colorScheme)}
                                onClick={this.onDiscardButtonClicked}>
                                {loc(discardButtonString)}
                            </DefaultButton>
                        </Customizer>
                    ) : (
                        /** Note: This is not using Customizer because the styling function uses themePrimary symbols
                         *  and the Button component does not suport IStyleOrFunctionObject.
                         *  https://github.com/OfficeDev/office-ui-fabric-react/issues/6134 tracks refactoring the button
                         *  component, scheduled to land by fabric's version 7.
                         */
                        <>
                            <DefaultButton
                                className={styles.actionButton}
                                text={loc(acceptButtonText)}
                                styles={rsvpButtonStyle}
                                onClick={this.onSend('Accept')}
                                iconProps={{ iconName: ControlIcons.Accept }}
                                disabled={isDeclinedSeriesInstance}
                            />
                            <DefaultButton
                                className={styles.actionButton}
                                text={loc(tentativeButtonText)}
                                onClick={this.onSend('Tentative')}
                                styles={rsvpButtonStyle}
                                iconProps={{ iconName: ControlIcons.Help }}
                                disabled={isDeclinedSeriesInstance}
                            />
                            <DefaultButton
                                className={styles.actionButton}
                                text={loc(declineButtonText)}
                                onClick={this.onSend('Decline')}
                                styles={rsvpButtonStyle}
                                iconProps={{ iconName: ControlIcons.Cancel }}
                            />
                        </>
                    )}
                </div>
            </div>
        );
    }

    private onDiscardButtonClicked = () => {
        logUsage('NotesEditorDiscarded');
        closeNotesEditor(this.props.id);
    };

    private renderFabricWellLabel = (calendarTheme: ITheme) => (recipientType: RecipientType) => {
        return (
            <RecipientWellLabelWithContactPicker
                recipientType={recipientType}
                addRecipient={this.addRecipientFromContactPicker}
                theme={calendarTheme}
            />
        );
    };

    private addRecipientFromContactPicker = (recipientToAdd: FindRecipientPersonaType) => {
        this.forwardRecipients.addItem(convertPersonaToRecipientViewState([recipientToAdd]));
    };

    private notifyOrganizerRef = ref => (this.notifyOrganizerToggle = ref);

    private forwardRecipientsRef = ref => (this.forwardRecipients = ref);

    private reactEditorRef = ref => (this.reactEditor = ref);

    componentDidMount() {
        const focusComponent: EditorFocusComponent =
            this.forwardRecipients ||
            this.reactEditor ||
            this.notifyOrganizerToggle ||
            this.sendButton;

        this.hotkeyDisposeFunctions.push(
            ...[
                addGlobalHotkey(getCommands().sendMeeting, this.onSend(null), {
                    isEnabled: this.useSendDiscardButtons,
                }),
            ]
        );

        // https://github.com/draft-js-plugins/draft-js-plugins/issues/357
        // if notes editor is opened directly and causes slab to initial load, the render -> componentDidMount get called before
        // the component has actually been inserted into the physical DOM (being loaded asynchronously), so we can't yet set focus here
        // instead we use a setTimeout with 0 delay to request focus setting to occur at the end of the async loading
        focusComponent && setTimeout(this.setFocusToComponent, 0, focusComponent);
    }

    componentWillUnmount() {
        if (this.reactEditor && this.props.isNarrow) {
            // Update the content to the viewState for undo.
            this.reactEditor.updateContentToViewState();
        }

        this.hotkeyDisposeFunctions.forEach(disposalFunctionPromise => {
            disposalFunctionPromise.then(disposalFunction => disposalFunction());
        });
        this.hotkeyDisposeFunctions = [];
    }

    private setFocusToComponent(focusableComponent: EditorFocusComponent) {
        return focusableComponent && focusableComponent.focus();
    }

    private onToggled = (sendResponse: boolean) =>
        onNotifyOrganizerToggled(this.props.id, sendResponse);

    private useSendDiscardButtons = () => {
        const viewState = getNotesEditorViewState(this.props.id);
        return (
            viewState &&
            !!(
                viewState.forwardees ||
                viewState.isTxpShareEvent ||
                viewState.isCancelledEvent ||
                viewState.proposedTime ||
                viewState.responseType
            )
        );
    };

    private onSend = (responseType: ResponseTypeType) => () => {
        let {
            content,
            eventScope,
            onSend,
            proposedTime,
            sendResponse,
            forwardees,
            isTxpShareEvent,
            openTimeStamp,
        } = getNotesEditorViewState(this.props.id);

        const hasForwardeesLengthError = forwardees && !forwardees.length;
        this.setState({ hasForwardeesLengthError: hasForwardeesLengthError });

        if (!hasForwardeesLengthError) {
            onSend(
                eventScope,
                isTxpShareEvent ? (this.plugins[0] as TxpContentPlugin).getTextContent() : content,
                responseType,
                proposedTime,
                sendResponse,
                forwardees,
                timestamp() - openTimeStamp
            );

            // onSend makes service calls which use notesEditorViewState, so the close (which clears state)
            // needs to happen after the send finishes
            closeNotesEditor(this.props.id);
        }
    };

    private getNoForwardeesErrorInfoBarViewState(visible: boolean): InfoBarMessageViewState[] {
        return visible
            ? [
                  {
                      key: 'hasForwardeesLengthError',
                      messageParts: [loc(errorMessageNoRecipentsMailCanNotBeSend)],
                      rank: InfoBarMessageRank.Info,
                      source: InfoBarMessageSource.Compose,
                  },
              ]
            : [];
    }
}
